// Basketball — The Hardwood Gauntlet. Ported from the CLAUDE DESIGN prototype
// (bb-gauntlet.jsx → GauntletScreen), wired to the real read hook (useGauntlet).
// The weekend 40-leg structured accumulator: a giant combined-odds hero, a
// legs-correct track with 30/35/38/40 milestones, and a 4×2 grid of round cards
// with jersey chips. HONEST by construction: ASSEMBLING/NON_PUBLICATION weeks
// render their own grand states; loading → <Loading/>; error → pill + dormant.
// No payout / stake figures — ever. Round names/theses are static design copy.

import {
  AwaitPanel, BbHead, Loading, fmtOdds, fmtTip,
} from "./_bbkit.tsx";
import { useGauntlet, type GauntletLeg, type GauntletSlip } from "./useBasketballProducts.ts";

const ROUND_NAMES: Record<number, { name: string; thesis: string }> = {
  1: { name: "B2B Harvest", thesis: "Tired legs · soft spreads" },
  2: { name: "Pace Traps", thesis: "Pace divergence · totals" },
  3: { name: "Referee Harvest", thesis: "Whistle · officiating" },
  4: { name: "Form Momentum", thesis: "Roll vs cold streak" },
  5: { name: "Efficiency Edges", thesis: "Net rating · TOV rate" },
  6: { name: "First Half Fortress", thesis: "Locked in 1H" },
  7: { name: "Global Hunt", thesis: "Off-radar leagues" },
  8: { name: "Conviction Picks", thesis: "Highest BFPE edge" },
};

const MILESTONES: ReadonlyArray<{ at: number; label: string; cls: string }> = [
  { at: 30, label: "Solid", cls: "" },
  { at: 35, label: "Elite", cls: "elite" },
  { at: 38, label: "Near miss", cls: "" },
  { at: 40, label: "Gauntlet falls", cls: "fall" },
];

type RoundStatus = "won" | "lost" | "progress" | "pending";
function roundStatus(legs: GauntletLeg[]): RoundStatus {
  if (legs.length === 0) return "pending";
  const won = legs.filter((l) => l.result === "WON").length;
  const lost = legs.filter((l) => l.result === "LOST").length;
  const pending = legs.filter((l) => l.result === "PENDING").length;
  if (pending === legs.length) return "pending";
  if (won === legs.length) return "won";
  if (lost > 0 && pending === 0) return "lost";
  return "progress";
}

function resultDot(r: GauntletLeg["result"]): string {
  if (r === "WON") return "won";
  if (r === "LOST") return "lost";
  if (r === "VOID") return "void";
  return "pending";
}

function fmtCombined(n: number | null | undefined): string {
  return typeof n === "number" && Number.isFinite(n) ? n.toFixed(2) : "—";
}

function GauntletBody({ slip }: { slip: GauntletSlip }): JSX.Element {
  const allLegs = Object.values(slip.rounds).flat();
  const correct = allLegs.filter((l) => l.result === "WON").length;
  const resolved = allLegs.filter((l) => l.result !== "PENDING").length;
  const legCount = slip.leg_count || 40;
  const pct = (correct / 40) * 100;

  return (
    <>
      <div className="bb-gauntlet-hero">
        <div className="top">
          <div>
            <div className="k">
              <span style={{ width: 24, height: 1, background: "currentColor", display: "inline-block" }} />
              40 legs · 8 rounds · every leg earned
            </div>
            <h2>The Hardwood<br />Gauntlet.</h2>
            <p className="lede">
              Eight rounds of conviction. One slip a week. The Gauntlet is built leg by leg through
              the working week — referee tape, rest math, efficiency edges, off-radar leagues. The
              math is not assembled; it is earned.
            </p>
          </div>
          <div className="odds">
            <div className="v">{fmtCombined(slip.combined_odds)}</div>
            <div className="u">combined odds · {legCount} legs</div>
          </div>
        </div>

        <div className="bb-gauntlet-track">
          <div className="bb-gauntlet-track-head">
            <span className="lab">Legs correct</span>
            <span className="cnt">{correct}<span className="max"> / {legCount}</span></span>
            <span style={{ marginLeft: "auto", fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-tertiary)", letterSpacing: "0.08em" }}>
              Resolved {resolved} · pending {legCount - resolved}
            </span>
          </div>
          <div className="bb-gauntlet-track-bar">
            <div className="fill" style={{ width: `${pct}%` }} />
            {MILESTONES.map((m) => (
              <div key={m.at} className="ms" style={{ left: `calc(${(m.at / 40) * 100}% - 1px)` }} />
            ))}
          </div>
          <div className="bb-gauntlet-milestones" style={{ position: "relative", height: 32 }}>
            {MILESTONES.map((m) => (
              <div key={m.at} className={`m ${m.cls}`} style={{ left: `${(m.at / 40) * 100}%` }}>
                <span className="n">{m.at}</span>
                <span>{m.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bb-section-title">
        <h3>The eight rounds</h3>
        <div className="sub">R1 → R8 · {slip.publication_time ?? "Saturday"} publication</div>
      </div>

      <div className="bb-rounds">
        {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => {
          const legs = slip.rounds[String(n)] ?? [];
          const status = roundStatus(legs);
          const meta = ROUND_NAMES[n]!;
          return (
            <div key={n} className="bb-round">
              <div className="rh">
                <div className={`bb-round-jersey ${status === "lost" ? "lost" : status === "won" ? "won" : ""}`}>
                  <span className="n">{n}</span>
                </div>
                <div className="rmeta">
                  <div className="nm">The {meta.name}</div>
                  <div className="th">{meta.thesis}</div>
                </div>
                <div className={`rs ${status}`}>{status === "progress" ? "In progress" : status === "pending" ? "Pending" : status}</div>
              </div>
              <div className="bb-round-legs">
                {legs.length > 0 ? legs.map((l, i) => (
                  <div key={`${l.game_id}-${i}`} className="bb-leg-mini">
                    <span className={`dot ${resultDot(l.result)}`} />
                    <span className="sel">{l.direction} · {l.market}</span>
                    <span className="od">{fmtOdds(l.leg_odds)}</span>
                  </div>
                )) : (
                  <div className="bb-leg-mini" style={{ color: "var(--text-tertiary)" }}>
                    <span className="dot pending" />
                    <span className="sel">Round assembling — legs confirmed by Thursday.</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 22, padding: "18px 22px", background: "var(--bg-inset)", border: "1px solid var(--border-faint)", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "space-between", fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: "0.08em", color: "var(--text-tertiary)" }}>
        <span>
          {slip.published_at ? `Published ${fmtTip(slip.published_at)}` : slip.computed_at ? `Assembled ${fmtTip(slip.computed_at)}` : `Published ${slip.publication_time ?? "Saturday"}`}
          {" "}· combined {fmtCombined(slip.combined_odds)} across {legCount} legs
        </span>
        <span>BFPE-verified · no fabrications</span>
      </div>
    </>
  );
}

export function GauntletScreen(): JSX.Element {
  const { loading, error, data } = useGauntlet();
  const ROUND_LIST = [1, 2, 3, 4, 5, 6, 7, 8];

  const assembling = !loading && (error != null || !data || data.status === "ASSEMBLING" || data.status === "VOID");
  const nonPub = !loading && data?.status === "NON_PUBLICATION";

  return (
    <div className="bb" data-screen-label="gauntlet">
      <BbHead kicker="THE HARDWOOD GAUNTLET" title="Basketball" />

      {loading ? (
        <Loading label="Loading the Gauntlet…" />
      ) : assembling ? (
        <>
          {error ? <AwaitPanel kicker="ERROR" icon="alert" title="Couldn't load the Gauntlet" body={error} ghosts={4} /> : null}
          <div className="bb-await-grand">
            <div className="k">ASSEMBLING · THE HARDWOOD GAUNTLET</div>
            <h3>The Gauntlet is assembling.</h3>
            <p>40 legs across 8 rounds. The slip publishes Saturday only when every round has cleared. We will not publish a Gauntlet that does not deserve the name.</p>
            <div className="bb-await-rounds-preview">
              {ROUND_LIST.map((n) => (
                <div key={n} className="gr">
                  <span style={{ position: "absolute", top: 8, left: 10, fontFamily: "var(--bb-display)", fontSize: 18, color: "var(--text-quaternary)" }}>R{n}</span>
                  <span>The {ROUND_NAMES[n]!.name}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : nonPub ? (
        <div className="bb-await-grand">
          <div className="k">NON-PUBLICATION · THE HARDWOOD GAUNTLET</div>
          <h3>Gauntlet paused this weekend.</h3>
          <p>{data?.non_publication_reason || "Insufficient qualifying signals. A weaker 40-leg slip is never published to keep a cadence. Back next Friday."}</p>
          <div className="bb-await-rounds-preview">
            {ROUND_LIST.map((n) => (
              <div key={n} className="gr">
                <span style={{ position: "absolute", top: 8, left: 10, fontFamily: "var(--bb-display)", fontSize: 18, color: "var(--text-quaternary)" }}>R{n}</span>
                <span>The {ROUND_NAMES[n]!.name}</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <GauntletBody slip={data!} />
      )}
    </div>
  );
}
